/**
 * 분석 대시보드(3-col 스티키) 레이아웃의 폭 모델.
 *
 * 왜 코드로 두는가 — 이 화면의 「AI 동정 후보」 표는 뷰포트가 아니라 **자기
 * 컨테이너 폭**을 기준으로 컬럼을 접는다(`FeaturesTable.tsx` 의 `@container` +
 * `@min-[600px]:table-cell`). 그래서 "몇 px 화면에서 몇 개 컬럼이 보이는가" 가
 * 클래스 문자열 여러 개에 흩어진 산수의 결과가 되고, 아무도 그걸 눈으로 계산할
 * 수 없다. 컬럼이 조용히 사라지는 것은 법과학 화면에서 설명 부담이 되므로
 * 산수를 한곳에 모아 테스트로 고정한다.
 *
 * jsdom 은 레이아웃을 계산하지 않아 실제 렌더 폭을 잴 수 없다. 대신 여기 값들이
 * 화면의 클래스와 일치하는지를 테스트가 확인한다(아래 상수 주석의 출처 참조).
 *
 * 근거: docs/2026/260828_인수인계_사이드바접기_검토.md §2 · §3.
 */

/** 전폭 라우트의 좌우 여백 한쪽. 출처: `Layout.tsx` `lg:px-8`. */
export const PAGE_PADDING_X_PX = 32;

/** 3-col 그리드와 중앙 안쪽 그리드의 간격. 출처: `SampleAnalysisPage.tsx` `gap-5`. */
export const GRID_GAP_PX = 20;

/**
 * 패널이 스스로 그리는 안쪽 여백 한쪽. 출처: `panelCls("standard")` 의 `p-3`.
 *
 * 2026-08-28 이전에는 페이지 카드가 `p-4`(한쪽 16px)를 한 겹 더 그려 패널마다
 * 56px 이 콘텐츠에서 빠졌다. 페이지 래퍼의 패딩을 걷어내 이 값만 남겼다.
 */
export const PANEL_PADDING_X_PX = 12;

/**
 * 페이지 카드의 테두리 한쪽. 출처: `SampleAnalysisPage.tsx` 의 `PANEL_CARD` 가
 * 가진 `border`(1px).
 *
 * 2026-08-28: 처음 이 모델을 쓸 때 빠뜨렸다가 **실제 브라우저 측정으로 잡았다**
 * — 모델이 2px 더 넓게 말하고 있었다(1440 펼침 기준 498.89 vs 실측 496.89).
 * 인수인계 문서 §2·§3 의 실측표도 같은 2px 을 빠뜨렸다(그래서 문서의 "1782px"
 * 은 실제로 1788px 이다). 임계 근처에서는 이 2px 이 판정을 뒤집을 수 있다.
 */
export const PANEL_BORDER_X_PX = 1;

/**
 * 「AI 동정 후보」 표가 저정보 컬럼(ID·관찰 #·물성)을 펴는 컨테이너 폭.
 * 출처: `FeaturesTable.tsx` 의 `@min-[600px]:table-cell`.
 */
export const FEATURES_TABLE_COLUMN_THRESHOLD_PX = 600;

/**
 * 클래식 세로 스크롤바의 폭. Chromium·Firefox on Windows·Linux 기준 15px.
 *
 * 2026-08-28: 이것도 처음엔 빠져 있었다. 분석 대시보드는 **항상 세로로 길어**
 * 문서 스크롤바가 뜨고(`index.css` 에 `scrollbar-gutter` 나 스크롤바 커스터마이즈가
 * 없다), 그만큼 `documentElement.clientWidth` 가 깎인다. 임계 600px 근처에서는
 * 이 15px 이 판정을 뒤집는다 — 실제로 1440 창에서 좌측만 접었을 때가 그렇다
 * (스크롤바 없다고 보면 606.67 → 표시, 실제로는 599.17 → 숨김).
 *
 * macOS 의 오버레이 스크롤바는 폭을 먹지 않지만, 이 화면의 대상 환경은
 * Windows 노트북이라 **있는 쪽을 기본값으로 잡는다**(없는 쪽은 여유가 늘 뿐이다).
 */
export const CLASSIC_SCROLLBAR_PX = 15;

/**
 * 창 폭 → 레이아웃 뷰포트 폭(`document.documentElement.clientWidth`).
 *
 * 아래 폭 함수들은 전부 **레이아웃 뷰포트**를 받는다. "1440 노트북" 처럼 창
 * 폭으로 말할 때는 반드시 이 함수를 거칠 것.
 */
export function layoutViewportWidth(
  windowWidthPx: number,
  scrollbarPx: number = CLASSIC_SCROLLBAR_PX,
): number {
  return windowWidthPx - scrollbarPx;
}

/**
 * 페이지 콘텐츠 폭 — 전폭 라우트라 max-w 제한이 없다.
 * 인자는 창 폭이 아니라 **레이아웃 뷰포트**다(`layoutViewportWidth` 참조).
 */
export function dashboardContentWidth(layoutViewportPx: number): number {
  return layoutViewportPx - 2 * PAGE_PADDING_X_PX;
}

/**
 * `grid-cols-12` 안에서 `col-span-{span}` 이 차지하는 폭.
 * 트랙 12개 + 간격 11개가 컨테이너를 채우고, span 은 그 사이 간격을 흡수한다.
 */
export function twelveColSpanWidth(
  containerPx: number,
  span: number,
  gapPx: number = GRID_GAP_PX,
): number {
  const trackPx = (containerPx - 11 * gapPx) / 12;
  return span * trackPx + (span - 1) * gapPx;
}

/** 접힌 사이드바(레일)의 폭. 아래 `DASHBOARD_GRID_COLS` 의 `3rem` 과 같아야 한다. */
export const RAIL_PX = 48;

/** 사이드바 접힘 상태. 세션 한정이며 어디에도 저장하지 않는다. */
export interface SidebarCollapse {
  left: boolean;
  right: boolean;
}

const EXPANDED: SidebarCollapse = { left: false, right: false };

/**
 * 3-col 트랙의 `grid-template-columns`.
 *
 * **펼침 상태는 기존 `grid-cols-12` + `col-span-2/7/3` 과 픽셀 동일하다.**
 * 3-col 구조는 2026-05-15 에 4사이클 반복 + 사용자 시각 검증으로 채택된 안이라
 * (탈락: PI 원안 6.69 / 탭 7.58 / col-4 3등분 6.16) 접기를 넣으면서 폭이
 * 달라지면 안 된다. 그래서 트랙을 비율(2fr/7fr/3fr)이 아니라 간격까지 반영한
 * calc 로 적는다 — 12분할에서 `col-span-2` 는 트랙 2개 + 그 사이 간격 1개다:
 *
 *   좌 = 2·(W − 11g)/12 + g   = W/6 − 5g/12·2 = W/6 − 16.6667px   (g = 20)
 *   우 = 3·(W − 11g)/12 + 2g  = W/4 − 3g/4    = 25% − 15px
 *   중앙 = 나머지(1fr)         = 7W/12 − 5g/12
 *
 * `minmax(0,1fr)` 은 Tailwind 의 `grid-cols-*` 기본과 같다 — 넓은 표가 트랙을
 * 밀어내지 못하게 한다.
 *
 * 클래스 문자열은 Tailwind 가 소스에서 그대로 찾아야 하므로 조립하지 않고
 * 리터럴로 적는다. 값이 위 상수와 어긋나지 않는지는 테스트가 확인한다.
 */
export const DASHBOARD_GRID_COLS = {
  expanded:
    "md:grid-cols-[calc(16.6667%_-_16.6667px)_minmax(0,1fr)_calc(25%_-_15px)]",
  leftCollapsed: "md:grid-cols-[3rem_minmax(0,1fr)_calc(25%_-_15px)]",
  rightCollapsed:
    "md:grid-cols-[calc(16.6667%_-_16.6667px)_minmax(0,1fr)_3rem]",
  bothCollapsed: "md:grid-cols-[3rem_minmax(0,1fr)_3rem]",
} as const;

/** 접힘 상태에 맞는 그리드 트랙 클래스. */
export function dashboardGridColsClass(collapse: SidebarCollapse): string {
  if (collapse.left && collapse.right) return DASHBOARD_GRID_COLS.bothCollapsed;
  if (collapse.left) return DASHBOARD_GRID_COLS.leftCollapsed;
  if (collapse.right) return DASHBOARD_GRID_COLS.rightCollapsed;
  return DASHBOARD_GRID_COLS.expanded;
}

/**
 * 중앙 차트 열의 폭. 접힌 쪽은 레일 폭만 차지한다.
 * 인자는 **레이아웃 뷰포트**다(`layoutViewportWidth` 참조).
 */
export function centerColumnWidth(
  layoutViewportPx: number,
  collapse: SidebarCollapse = EXPANDED,
): number {
  const contentPx = dashboardContentWidth(layoutViewportPx);
  const leftPx = collapse.left ? RAIL_PX : twelveColSpanWidth(contentPx, 2);
  const rightPx = collapse.right ? RAIL_PX : twelveColSpanWidth(contentPx, 3);
  return contentPx - leftPx - rightPx - 2 * GRID_GAP_PX;
}

/**
 * 「AI 동정 후보」 표의 `@container` 실폭.
 * 중앙 열 안쪽 12분할의 `lg:col-span-8` 에서 카드 테두리와 패널 여백을 뺀 값.
 * 인자는 **레이아웃 뷰포트**다(`layoutViewportWidth` 참조).
 *
 * 실제 Chromium 측정과 일치한다 — 스크롤바 있는 1440 창(레이아웃 뷰포트 1425)
 * 에서 펼침 491.06 · 좌측만 접힘 599.17 · 좌우 접힘 784.00.
 */
export function candidateTableContainerWidth(
  layoutViewportPx: number,
  collapse: SidebarCollapse = EXPANDED,
): number {
  const cellPx = twelveColSpanWidth(
    centerColumnWidth(layoutViewportPx, collapse),
    8,
  );
  return cellPx - 2 * PANEL_BORDER_X_PX - 2 * PANEL_PADDING_X_PX;
}

/**
 * 중앙 열 안쪽 「TIC | 질량스펙트럼」 한 줄의 12분할 배분 (2026-09-06).
 *
 * 종전 6:6 에서 **7:5** 로 옮겼다. 근거는 면적이 정보량·조작 중심성과 반대로
 * 배분돼 있었다는 것이다 — 1280 창·펼침 실측에서 TIC 이 그리는 폭은 221px 인데
 * 원자료는 12,270점(화면이 스스로 "1픽셀 폭(원자료 약 16점)" 이라고 말한다),
 * 바로 위 m/z vs RT Plot 은 596px 에 서버가 500점으로 줄인 자료를 그린다.
 * 그리고 드래그·클릭하는 대상이 바로 그 TIC 이다.
 *
 * 전폭(12:0)이 아니라 7:5 인 이유는 운영자 판단이다 — 기존 UI 변경 폭을 줄인다.
 */
export const TIC_SPAN = 7;
export const MASS_SPECTRUM_SPAN = 5;

/**
 * 질량스펙트럼이 유지해야 할 최소 그림 폭(px).
 *
 * `MassSpectrum.tsx` 는 막대 폭을 **10px 로 고정** 해서 그린다(`barSize={10}`).
 * 그리는 폭이 줄어도 막대는 줄지 않으므로, 좁아지면 이웃한 m/z 피크가 겹쳐
 * 한 덩어리가 된다. 실브라우저 확인(1280 창): 8:4(그림 폭 128px)에서 낮은 m/z
 * 피크 무리가 뭉치고 패널 제목이 3줄로 접혔다. 7:5(184px)는 구분이 유지된다.
 *
 * 값 150 은 그 사이에 둔 하한이다 — TIC 을 더 넓히려는 다음 변경이 질량
 * 스펙트럼을 조용히 뭉개면 테스트가 먼저 걸린다.
 */
export const MASS_SPECTRUM_MIN_PLOT_PX = 150;

/** uPlot 축 furniture 한 줄분(좌 64 + 우 25). 출처: `TICPlotUplot.tsx` 축 설정. */
const UPLOT_AXIS_X_PX = 89;

/** recharts 기본 YAxis 폭 + 우측 여백. 출처: `MassSpectrum.tsx` 의 축 구성. */
const RECHARTS_AXIS_X_PX = 70;

function innerPanelContentWidth(
  layoutViewportPx: number,
  collapse: SidebarCollapse,
  span: number,
): number {
  const cellPx = twelveColSpanWidth(
    centerColumnWidth(layoutViewportPx, collapse),
    span,
  );
  return cellPx - 2 * PANEL_BORDER_X_PX - 2 * PANEL_PADDING_X_PX;
}

/** TIC 이 실제로 곡선을 그리는 폭. 인자는 **레이아웃 뷰포트**다. */
export function ticPlotWidth(
  layoutViewportPx: number,
  collapse: SidebarCollapse = EXPANDED,
  span: number = TIC_SPAN,
): number {
  return (
    innerPanelContentWidth(layoutViewportPx, collapse, span) - UPLOT_AXIS_X_PX
  );
}

/** 질량스펙트럼이 실제로 막대를 그리는 폭. 인자는 **레이아웃 뷰포트**다. */
export function massSpectrumPlotWidth(
  layoutViewportPx: number,
  collapse: SidebarCollapse = EXPANDED,
  span: number = MASS_SPECTRUM_SPAN,
): number {
  return (
    innerPanelContentWidth(layoutViewportPx, collapse, span) -
    RECHARTS_AXIS_X_PX
  );
}
